import { Stack } from '@mui/material';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import {
  CostCenterDto,
  OrderActionEnum,
  OrderDto,
  OrderState,
  ProductDto,
} from 'desk-common';
import { OrderCostCenterField } from './OrderCostCenterField';
import { useOrderFormMutations } from '../../hooks/useOrderFormMutations';
import { OrderItemsList } from './OrderItemsList';
import {
  DEFAULT_FORM_VALUES,
  EMPTY_ITEM,
  FormValues,
} from './constants/orderForm.constants';
import {
  calculateOrderTotal,
  getInitialCreateValues,
  getOrderFormValues,
  getOrderPermissions,
} from './OrderForm.helpers';
import useUser from '../../auth/useUser';

interface Props {
  order?: OrderDto | null;
  onSuccess?: () => void;
  onSubmitSettled?: () => void;
  onTotalChange?: (total: number) => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
  onCostCenterChange?: (costCenterId: number | null) => void;
  onItemCountChange?: (count: number | null) => void;
  costCenters: CostCenterDto[];
  products: ProductDto[];
}

export interface OrderFormHandle {
  submit: (action: OrderActionEnum) => Promise<void>;
}

const OrderForm = forwardRef<OrderFormHandle, Props>(
  (
    {
      order,
      onSuccess,
      onSubmitSettled,
      onTotalChange,
      onUnsavedChangesChange,
      onCostCenterChange,
      onItemCountChange,
      costCenters,
      products,
    },
    ref
  ) => {
    const [itemsAlert, setItemsAlert] = useState<string | null>(null);
    const user = useUser();

    const { isEdit, isEditable, isReadOnly } = getOrderPermissions(order, user);

    const hasSingleCostCenter = costCenters.length === 1;

    let singleCostCenterId: number | null = null;

    if (hasSingleCostCenter) {
      singleCostCenterId = costCenters[0].id;
    }

    const form = useForm<FormValues>({
      defaultValues: DEFAULT_FORM_VALUES,
    });

    const {
      control,
      setValue,
      register,
      handleSubmit,
      reset,
      formState: { errors, isDirty: hasUnsavedChanges },
    } = form;

    const { fields, append, remove } = useFieldArray({
      control,
      name: 'items',
    });

    useEffect(() => {
      let values;

      if (order) {
        values = getOrderFormValues(order);
      } else {
        values = getInitialCreateValues(singleCostCenterId);
      }

      reset(values);
    }, [order, singleCostCenterId, reset]);

    const watchedItems = useWatch({
      control,
      name: 'items',
    });

    const items = useMemo(() => {
      if (watchedItems) {
        return watchedItems;
      }

      return [];
    }, [watchedItems]);

    const total = useMemo(() => {
      return calculateOrderTotal(items);
    }, [items]);

    useEffect(() => {
      if (onTotalChange) {
        onTotalChange(total);
      }
    }, [total, onTotalChange]);

    // Inform parent component about unsaved changes
    useEffect(() => {
      onUnsavedChangesChange?.(hasUnsavedChanges);
    }, [hasUnsavedChanges, onUnsavedChangesChange]);

    const watchedCostCenterId = useWatch({
      control,
      name: 'costCenterId',
    });

    useEffect(() => {
      onCostCenterChange?.(watchedCostCenterId);
    }, [watchedCostCenterId, onCostCenterChange]);

    useEffect(() => {
      const validItemsCount = items.filter((item) => item.productId).length;
      onItemCountChange?.(validItemsCount || null);
    }, [items, onItemCountChange]);

    const selectedProductIds = useMemo(() => {
      const ids: number[] = [];

      for (const item of items) {
        if (item.productId) {
          ids.push(item.productId);
        }
      }

      return ids;
    }, [items]);

    useEffect(() => {
      const hasValidProduct = items.some((item) => item?.productId != null);

      if (hasValidProduct && itemsAlert) {
        setItemsAlert(null);
      }
    }, [items, itemsAlert]);

    const { createMutation, updateMutation } = useOrderFormMutations({
      isEdit,
      reset,
      onSuccess,
      onSubmitSettled,
    });

    const submit = useCallback(
      async (action: OrderActionEnum) => {
        await handleSubmit(
          async (data) => {
            try {
              const { costCenterId, items: formItems, ...restData } = data;

              const hasValidProduct = formItems.some(
                (item) => item.productId != null
              );

              if (!hasValidProduct) {
                const message = 'Debe agregar al menos un producto';

                setItemsAlert(message);
                toast.error(message);
                onSubmitSettled?.();
                return;
              }

              setItemsAlert(null);

              const payloadItems = formItems.map((item) => ({
                productId: item.productId as number,
                quantity: item.quantity,
                unitPrice: item.price,
              }));

              if (isEdit && order) {
                await updateMutation({
                  ...restData,
                  costCenterId: costCenterId as number,
                  items: payloadItems,
                  action,
                  id: order.id,
                  state: OrderState.BORRADOR,
                });
              } else {
                await createMutation({
                  ...restData,
                  costCenterId: costCenterId as number,
                  items: payloadItems,
                  action,
                });
              }
            } catch (_error) {
              toast.error('Ocurrió un error al procesar la orden');
            }
          },
          () => {
            onSubmitSettled?.();
          }
        )();
      },
      [
        isEdit,
        order,
        updateMutation,
        createMutation,
        handleSubmit,
        onSubmitSettled,
      ]
    );

    useImperativeHandle(ref, () => ({
      submit,
    }));

    const handleAppendItem = () => {
      append({ ...EMPTY_ITEM });
    };

    const handleClearItems = () => {
      remove();
      append({ ...EMPTY_ITEM });
    };

    return (
      <Stack spacing={3}>
        <OrderCostCenterField
          control={control}
          errors={errors}
          costCenters={costCenters}
          hasSingleCostCenter={hasSingleCostCenter}
          isReadOnly={isReadOnly}
        />

        <OrderItemsList
          fields={fields}
          items={items}
          itemsAlert={itemsAlert}
          control={control}
          register={register}
          setValue={setValue}
          errors={errors}
          products={products}
          selectedProductIds={selectedProductIds}
          isReadOnly={isReadOnly}
          isEditable={isEditable}
          remove={remove}
          onAppendItem={handleAppendItem}
          onClearItems={handleClearItems}
        />
      </Stack>
    );
  }
);

OrderForm.displayName = 'OrderForm';

export default OrderForm;
